(() => {
  if (customElements.get('cooldo-delivery-estimate')) return;

  class CooldoDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.stop();
      const number = (key, fallback, max) => {
        const value = this.dataset[key];
        return value !== '' && Number.isFinite(Number(value))
          ? Math.max(0, Math.min(max, Math.floor(Number(value)))) : fallback;
      };
      this.rules = {
        hour: number('cutoffHour', 15, 23),
        minute: number('cutoffMinute', 0, 59),
        processing: number('processingDays', 0, 14),
        afterCutoff: number('afterCutoffDays', 1, 7),
        min: number('transitMin', 1, 30),
        max: number('transitMax', 2, 30),
        business: this.dataset.businessDays !== 'false'
      };
      this.rules.max = Math.max(this.rules.min, this.rules.max);
      const options = {
        timeZone: this.dataset.timeZone || 'Europe/Madrid',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
      };
      try {
        this.clock = new Intl.DateTimeFormat('en-GB', options);
      } catch {
        this.clock = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'Europe/Madrid' });
      }
      this.dateFormat = new Intl.DateTimeFormat(document.documentElement.lang || 'es', {
        timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric'
      });
      this.update();
      this.hidden = false;
      this.style.removeProperty('display');
      this.timer = window.setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() { this.stop(); }
    stop() { window.clearInterval(this.timer); }

    civilDate(instant) {
      const parts = Object.fromEntries(this.clock.formatToParts(instant).map(part => [part.type, part.value]));
      return new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second));
    }

    epoch(civil) {
      let instant = civil.getTime();
      for (let count = 0; count < 3; count += 1) {
        instant += civil.getTime() - this.civilDate(new Date(instant)).getTime();
      }
      return instant;
    }

    eligible(date) { return !this.rules.business || ![0, 6].includes(date.getUTCDay()); }

    addDays(date, days) {
      const next = new Date(date);
      while (days > 0) {
        next.setUTCDate(next.getUTCDate() + 1);
        if (this.eligible(next)) days -= 1;
      }
      return next;
    }

    calculate(instant) {
      const now = this.civilDate(instant);
      const todayCutoff = new Date(now);
      todayCutoff.setUTCHours(this.rules.hour, this.rules.minute, 0, 0);
      let cutoff = new Date(todayCutoff);
      if (now >= cutoff) cutoff.setUTCDate(cutoff.getUTCDate() + 1);
      while (!this.eligible(cutoff)) cutoff.setUTCDate(cutoff.getUTCDate() + 1);
      let accepted = new Date(now);
      accepted.setUTCHours(0, 0, 0, 0);
      if (!this.eligible(accepted)) {
        do { accepted.setUTCDate(accepted.getUTCDate() + 1); } while (!this.eligible(accepted));
      } else if (now >= todayCutoff) {
        accepted = this.addDays(accepted, this.rules.afterCutoff);
      }
      const ship = this.addDays(accepted, this.rules.processing);
      return {
        beforeCutoff: this.eligible(now) && now < todayCutoff,
        order: now,
        ship,
        start: this.addDays(ship, this.rules.min),
        end: this.addDays(ship, this.rules.max),
        seconds: Math.max(0, Math.floor((this.epoch(cutoff) - instant.getTime()) / 1000))
      };
    }

    update() {
      const result = this.calculate(new Date());
      const pad = value => String(value).padStart(2, '0');
      const values = {
        hora: `${pad(this.rules.hour)}:${pad(this.rules.minute)}`,
        desde: this.dateFormat.format(result.start),
        hasta: this.dateFormat.format(result.end)
      };
      const copy = text => (text || '').replace(/\{(hora|desde|hasta)\}/g, (_, key) => values[key]);
      const set = (selector, text) => {
        const element = this.querySelector(selector);
        if (element && element.textContent !== text) element.textContent = text;
      };
      set('[data-delivery-title]', copy(result.beforeCutoff ? this.dataset.title : this.dataset.afterCutoffTitle));
      const timer = this.querySelector('[data-delivery-timer]');
      if (timer) timer.hidden = !result.beforeCutoff;
      set('[data-delivery-range]', copy(this.dataset.rangeCopy));
      set('[data-countdown-hours]', pad(Math.floor(result.seconds / 3600)));
      set('[data-countdown-minutes]', pad(Math.floor(result.seconds % 3600 / 60)));
      set('[data-countdown-seconds]', pad(result.seconds % 60));
      set('[data-order-date]', this.dateFormat.format(result.order));
      set('[data-ship-date]', this.dateFormat.format(result.ship));
      set('[data-delivery-date]', `${values.desde} - ${values.hasta}`);
    }
  }

  customElements.define('cooldo-delivery-estimate', CooldoDeliveryEstimate);
})();
